import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShopping } from '../contexts/ShoppingContext';
import magic from '../lib/magic';
import { useNavigate } from 'react-router-dom';

const POSTER_SIZES = [
  { id: 'a5', name: 'A5', price: 12.92, stripeId: 'price_1QH8TzGOnDBMaozTLbj4CXXh' },
  { id: 'a4', name: 'A4', price: 13.92, stripeId: 'price_1QH8TiGOnDBMaozTWP2YzS0s' },
  { id: 'a3', name: 'A3', price: 19.27, stripeId: 'price_1QH8UHGOnDBMaozTKdwrbG2l' },
  { id: 'a2', name: 'A2', price: 26.00, stripeId: 'price_1QH8UXGOnDBMaozTWyxF6RK2' },
  { id: 'a1', name: 'A1', price: 35.00, stripeId: 'price_1QH8UoGOnDBMaozTRftrbagj' },
  { id: 'a0', name: 'A0', price: 55.00, stripeId: 'price_1QH8V3GOnDBMaozTsH47As2i' },
];

const CartDrawer = ({ isOpen, onClose }) => {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const { 
    cartItems, 
    removeFromCart, 
    updateCartItemSize,
    updateCartItemQuantity,
    clearCart
  } = useShopping();
  const navigate = useNavigate();

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const size = POSTER_SIZES.find(s => s.id === item.selectedSize);
      return total + (size ? size.price * item.quantity : 0);
    }, 0);
  };

  const handleCheckout = async () => {
    try {
      setIsCheckingOut(true);

      let didToken;
      try {
        didToken = await magic.user.getIdToken({ lifespan: 900 });
      } catch (tokenError) {
        console.error('Token error:', tokenError);
        if (tokenError.message.includes('failed to load')) {
          navigate('/login');
          return;
        }
        throw tokenError;
      }

      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${didToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'poster',
          items: cartItems.map(item => ({
            ...item,
            stripeId: POSTER_SIZES.find(s => s.id === item.selectedSize)?.stripeId,
            price: POSTER_SIZES.find(s => s.id === item.selectedSize)?.price
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Checkout failed');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.message.includes('failed to load')) {
        alert('Your session has expired. Please log in again.');
        navigate('/login');
      } else {
        alert('Failed to start checkout. Please try again.');
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Your Cart</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cartItems.length === 0 ? (
                <p className="text-center text-gray-500">Your cart is empty</p>
              ) : (
                cartItems.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="aspect-w-3 aspect-h-4 mb-4">
                      <img
                        src={item.url}
                        alt={`Cart item ${index + 1}`}
                        className="rounded-lg object-cover"
                      />
                    </div>
                    <div className="space-y-3">
                      {/* Size Selection - Updated with warning styles */}
                      <div className="relative">
                        <select
                          value={item.selectedSize || ''}
                          onChange={(e) => updateCartItemSize(item, e.target.value)}
                          className={`w-full rounded-lg py-2 px-3 text-sm transition-all duration-300 ${
                            !item.selectedSize 
                              ? 'border-2 border-red-500 bg-red-50 animate-pulse' 
                              : 'border border-gray-200'
                          }`}
                        >
                          <option value="" className="text-gray-400">
                            Select poster size *
                          </option>
                          {POSTER_SIZES.map((size) => (
                            <option key={size.id} value={size.id}>
                              {size.name} - £{size.price}
                            </option>
                          ))}
                        </select>
                        {!item.selectedSize && (
                          <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                            Please select a size
                          </p>
                        )}
                      </div>

                      {/* Quantity and Remove Controls */}
                      <div className="flex items-center justify-between gap-4 mt-6">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Qty:</span>
                          <select
                            value={item.quantity}
                            onChange={(e) => updateCartItemQuantity(item, e.target.value)}
                            className="rounded-lg border-gray-200 py-1 px-2 text-sm w-16"
                          >
                            {[1,2,3,4,5,6,7,8,9,10].map(num => (
                              <option key={num} value={num}>{num}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => removeFromCart(item)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Item Total */}
                      {item.selectedSize && (
                        <div className="text-right text-sm text-gray-600">
                          Item total: £{(POSTER_SIZES.find(s => s.id === item.selectedSize)?.price * item.quantity).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="border-t border-gray-200 p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total:</span>
                  <span className="font-bold">£{calculateTotal().toFixed(2)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={!cartItems.every(item => item.selectedSize) || isCheckingOut}
                  className="relative w-full py-3 px-6 text-center text-white bg-gradient-to-r from-[#FF3366] to-[#7C3AED] rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                >
                  {isCheckingOut ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>Redirecting to Checkout...</span>
                    </div>
                  ) : (
                    'Checkout'
                  )}
                </button>
              </div>
            )}

            {/* Add loading overlay */}
            {isCheckingOut && (
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white p-4 rounded-xl shadow-lg flex items-center space-x-3">
                  <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-700">Preparing Checkout...</span>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer; 